import moment from 'moment';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomDatePicker from 'src/components/CustomDatePicker';
import { showError } from 'src/helpers/error.helper';
import { updateCoverageDate } from 'src/pages/payment-msi/PaymentMsiV2/api/update-coverage-date.api';
import { getCoverageInputRanges } from 'src/pages/payment-msi/PaymentMsiV2/utils/get-coverage-input-ranges.util';
import { updatePurchasePolicyInput } from 'src/redux/actions/purchasePolicy';
import {
  getPurchasePolicyAdditionalData,
  getPurchasePolicyInput,
} from 'src/redux/selectors/purchasePolicy';
import { getCompanyVendor } from 'src/redux/selectors/shared';

type LoadingStatus = 'idle' | 'pending' | 'resolved';

const coverageStartFieldStyles = {
  input: {
    borderRadius: '4px',
  },
};

export const CoverageStartField = () => {
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>('idle');

  const dispatch = useDispatch();
  const purchasePolicyInput = useSelector(getPurchasePolicyInput);
  const purchasePolicyAdditionalData = useSelector(
    getPurchasePolicyAdditionalData,
  );
  const companyVendor = useSelector(getCompanyVendor);

  const { minDate, maxDate } = getCoverageInputRanges();

  async function onCoverageStartChange(value: Date | null) {
    if (!value) {
      return;
    }

    setLoadingStatus('pending');

    const newCoverageStart = moment(value).toISOString();

    try {
      dispatch(updatePurchasePolicyInput({ coverageStart: newCoverageStart }));

      const { data, errors } = await updateCoverageDate({
        companyVendor,
        coverageStart: newCoverageStart,
        purchasePolicyAdditionalData,
        purchasePolicyInput,
      });

      if (errors) {
        const errorMessage = errors[0].message;
        showError(errorMessage);
        return;
      }

      dispatch(updatePurchasePolicyInput(data));
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unknown error occurred while updating the coverage date.';

      showError(errorMessage);
    }

    setLoadingStatus('resolved');
  }

  return (
    <CustomDatePicker
      label={'Coverage Start Date'}
      value={purchasePolicyInput.coverageStart}
      onChange={onCoverageStartChange}
      disabled={loadingStatus === 'pending'}
      minDate={minDate}
      maxDate={maxDate}
      InputProps={{
        sx: coverageStartFieldStyles.input,
      }}
    />
  );
};
